import { ArrowRight, MapPin, Globe, Plane, Briefcase, Home, FileCheck, CheckCircle } from 'lucide-react';
import { Link } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';

const NewZealandVisas = () => {
  const visaTypes = [
    {
      icon: Plane,
      title: 'Visitor Visa',
      description: 'Short-term visits for tourism, family visits, or business meetings',
      duration: 'Up to 9 months',
      suitableFor: 'Tourists, business visitors, family visits'
    },
    {
      icon: Briefcase,
      title: 'Work Visa',
      description: 'Employment opportunities with New Zealand employers',
      duration: 'Varies by visa type',
      suitableFor: 'Skilled workers with job offers'
    },
    {
      icon: Home,
      title: 'Residence Pathway',
      description: 'Permanent residence options including Skilled Migrant Category',
      duration: 'Permanent',
      suitableFor: 'Skilled professionals, investors, entrepreneurs'
    },
    {
      icon: FileCheck,
      title: 'Documentation Support',
      description: 'Complete assistance with forms, supporting documents, and compliance',
      duration: 'Throughout process',
      suitableFor: 'All visa applicants'
    }
  ];

  const whyNZ = [
    'High quality of life',
    'Strong economy with job opportunities',
    'World-class education system',
    'Beautiful natural environment',
    'Multicultural and welcoming society',
    'Pathway to permanent residence'
  ];

  const ourExpertise = [
    {
      title: 'Policy Knowledge',
      description: 'Up-to-date understanding of Immigration New Zealand requirements and updates'
    },
    {
      title: 'Documentation Excellence',
      description: 'Thorough preparation of all forms and supporting evidence'
    },
    {
      title: 'Eligibility Assessment',
      description: 'Honest evaluation of your profile and best pathway recommendations'
    },
    {
      title: 'Application Strategy',
      description: 'Tailored approach to present your case in the strongest manner'
    },
    {
      title: 'Compliance Focus',
      description: 'Ensuring all requirements are met to avoid delays or rejections'
    },
    {
      title: 'Post-Submission Support',
      description: 'Guidance and updates throughout the decision-making process'
    }
  ];

  return (
    <div className="min-h-screen">
      {/* Hero Section */}
      <section className="relative bg-gradient-to-br from-blue-600 via-blue-700 to-teal-600 text-white py-24 overflow-hidden">
        <div className="absolute inset-0 bg-grid-pattern opacity-10"></div>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div>
              <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold mb-6 leading-tight">
                New Zealand Visa Specialists
              </h1>
              <p className="text-xl text-blue-100 mb-8 leading-relaxed">
                Expert guidance for your New Zealand visa journey - from visitor visas to permanent residence pathways
              </p>
              <div className="flex flex-wrap gap-4">
                <Button asChild size="lg" className="bg-white text-blue-600 hover:bg-gray-100">
                  <Link to="/eligibility">
                    Check Your Eligibility
                    <ArrowRight className="ml-2 h-5 w-5" />
                  </Link>
                </Button>
                <Button asChild size="lg" variant="outline" className="border-2 border-white text-white hover:bg-white/10">
                  <Link to="/contact">
                    Contact Specialist
                  </Link>
                </Button>
              </div>
            </div>
            <div className="relative">
              <img
                src="https://images.unsplash.com/photo-1486406146926-c627a92ad1ab"
                alt="New Zealand cityscape"
                className="rounded-2xl shadow-2xl w-full h-[400px] object-cover"
              />
              <div className="absolute -bottom-6 -right-6 bg-white text-gray-900 p-6 rounded-xl shadow-2xl">
                <div className="flex items-center gap-3">
                  <MapPin className="h-8 w-8 text-blue-600" />
                  <div>
                    <p className="text-sm text-gray-600">Specialized In</p>
                    <p className="text-xl font-bold">NZ Immigration</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Visa Types */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl sm:text-4xl font-bold text-gray-900 mb-4">
              New Zealand Visa Categories We Specialize In
            </h2>
            <p className="text-lg text-gray-600 max-w-2xl mx-auto">
              Comprehensive support for all major NZ visa pathways
            </p>
          </div>

          <div className="grid md:grid-cols-2 gap-8">
            {visaTypes.map((visa, idx) => (
              <Card key={idx} className="border-2 border-gray-200 hover:border-blue-300 hover:shadow-xl transition-all group">
                <CardContent className="p-8">
                  <div className="w-14 h-14 bg-gradient-to-br from-blue-100 to-teal-100 rounded-xl flex items-center justify-center mb-4 group-hover:scale-110 transition-transform">
                    <visa.icon className="h-7 w-7 text-blue-600" />
                  </div>
                  <h3 className="text-2xl font-bold text-gray-900 mb-3">{visa.title}</h3>
                  <p className="text-gray-700 mb-4">{visa.description}</p>
                  <div className="space-y-2 text-sm">
                    <p className="text-gray-600">
                      <span className="font-semibold">Duration:</span> {visa.duration}
                    </p>
                    <p className="text-gray-600">
                      <span className="font-semibold">Suitable For:</span> {visa.suitableFor}
                    </p>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Why New Zealand */}
      <section className="py-20 bg-gradient-to-br from-gray-50 to-blue-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div>
              <h2 className="text-3xl sm:text-4xl font-bold text-gray-900 mb-6">
                Why Choose New Zealand?
              </h2>
              <p className="text-lg text-gray-700 mb-8">
                New Zealand offers exceptional opportunities for professionals, students, and families seeking a better quality of life.
              </p>
              <div className="grid sm:grid-cols-2 gap-4">
                {whyNZ.map((reason, idx) => (
                  <div key={idx} className="flex items-start gap-3">
                    <CheckCircle className="h-5 w-5 text-green-600 flex-shrink-0 mt-1" />
                    <span className="text-gray-700">{reason}</span>
                  </div>
                ))}
              </div>
            </div>
            <div>
              <Card className="overflow-hidden border-2 border-gray-200 shadow-xl">
                <img
                  src="https://images.pexels.com/photos/8292854/pexels-photo-8292854.jpeg"
                  alt="New Zealand opportunities"
                  className="w-full h-[400px] object-cover"
                />
              </Card>
            </div>
          </div>
        </div>
      </section>

      {/* Our Expertise */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl sm:text-4xl font-bold text-gray-900 mb-4">
              Our New Zealand Visa Expertise
            </h2>
            <p className="text-lg text-gray-600 max-w-2xl mx-auto">
              What sets our NZ visa services apart
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {ourExpertise.map((item, idx) => (
              <Card key={idx} className="border-2 border-gray-200 hover:border-blue-300 hover:shadow-lg transition-all">
                <CardContent className="p-6">
                  <div className="flex items-center gap-3 mb-3">
                    <div className="w-8 h-8 bg-gradient-to-r from-blue-600 to-teal-600 rounded-full flex items-center justify-center flex-shrink-0">
                      <span className="text-white text-sm font-bold">{idx + 1}</span>
                    </div>
                    <h3 className="text-lg font-bold text-gray-900">{item.title}</h3>
                  </div>
                  <p className="text-gray-600 text-sm">{item.description}</p>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Process Section */}
      <section className="py-20 bg-gradient-to-br from-blue-600 via-blue-700 to-teal-600 text-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl sm:text-4xl font-bold mb-4">
              Our NZ Visa Process
            </h2>
            <p className="text-lg text-blue-100">
              Step-by-step guidance from assessment to approval
            </p>
          </div>

          <div className="space-y-6">
            {[
              { step: 1, title: 'Free Profile Assessment', desc: 'We evaluate your eligibility for different NZ visa categories' },
              { step: 2, title: 'Pathway Recommendation', desc: 'Honest advice on the best visa option for your situation' },
              { step: 3, title: 'Document Preparation', desc: 'Comprehensive checklist and assistance with all required documents' },
              { step: 4, title: 'Application Review', desc: 'Thorough verification before submission to Immigration New Zealand' },
              { step: 5, title: 'Submission & Tracking', desc: 'Application filing and regular status updates' },
              { step: 6, title: 'Post-Decision Support', desc: 'Guidance on next steps after visa decision' }
            ].map((item) => (
              <div key={item.step} className="bg-white/10 backdrop-blur-sm rounded-xl p-6 border border-white/20 hover:bg-white/20 transition-all">
                <div className="flex items-start gap-4">
                  <div className="w-10 h-10 bg-white text-blue-600 rounded-full flex items-center justify-center font-bold flex-shrink-0">
                    {item.step}
                  </div>
                  <div>
                    <h3 className="text-xl font-bold mb-2">{item.title}</h3>
                    <p className="text-blue-100">{item.desc}</p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <Globe className="h-16 w-16 text-blue-600 mx-auto mb-6" />
          <h2 className="text-3xl sm:text-4xl font-bold text-gray-900 mb-4">
            Ready to Explore New Zealand Opportunities?
          </h2>
          <p className="text-lg text-gray-600 mb-8 max-w-2xl mx-auto">
            Let our specialists assess your profile and guide you through the NZ visa process
          </p>
          <div className="flex flex-wrap justify-center gap-4">
            <Button asChild size="lg" className="bg-gradient-to-r from-blue-600 to-teal-600 hover:from-blue-700 hover:to-teal-700 text-white">
              <Link to="/eligibility">
                Check NZ Visa Eligibility
                <ArrowRight className="ml-2 h-5 w-5" />
              </Link>
            </Button>
            <Button asChild size="lg" variant="outline" className="border-2 border-blue-600 text-blue-600 hover:bg-blue-50">
              <a href="tel:+6422160146">
                Call NZ Office: +64 221601146
              </a>
            </Button>
          </div>
        </div>
      </section>
    </div>
  );
};

export default NewZealandVisas;
